import React, { useState, useEffect } from "react";
import {
  X,
  Building,
  CreditCard,
  Smartphone,
  Clock,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { toast } from "react-hot-toast";
import {
  initiateStudentEquityPayment,
  getPaymentStatus,
} from "../../services/fees";
import { formatKSh } from "../../utils/currency";

interface EquityPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  maxAmount: number;
  studentInfo: {
    name: string;
    studentId: string;
    id: string; // MongoDB _id
  };
  onPaymentSuccess: () => void;
}

const EquityPaymentModal: React.FC<EquityPaymentModalProps> = ({
  isOpen,
  onClose,
  maxAmount,
  studentInfo,
  onPaymentSuccess,
}) => {
  const [amount, setAmount] = useState(maxAmount.toString());
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showWaitingModal, setShowWaitingModal] = useState(false);
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);

  // Reset modal state
  const resetModal = () => {
    setAmount(maxAmount.toString());
    setPhoneNumber("");
    setIsProcessing(false);
    setShowWaitingModal(false);
    setPaymentId(null);
    setPaymentStatus(null);
  };

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      resetModal();
    }
  }, [isOpen, maxAmount]);

  // Poll for payment status when waiting
  useEffect(() => {
    if (!showWaitingModal || !paymentId) {
      console.log("Polling not started:", { showWaitingModal, paymentId });
      return;
    }

    let pollCount = 0;
    const maxPolls = 100; // 100 polls * 3 seconds = 5 minutes

    const pollPaymentStatus = async () => {
      pollCount++;
      console.log(`=== Poll #${pollCount} ===`);

      try {
        console.log("Polling payment status for paymentId:", paymentId);
        const response = await getPaymentStatus(paymentId);
        console.log(
          "Full payment status response:",
          JSON.stringify(response, null, 2)
        );

        // Handle both wrapped response formats
        const paymentData = response.success ? response.data : response;

        if (paymentData) {
          const status = (paymentData as any).status;
          const verificationStatus = (paymentData as any).verificationStatus;

          console.log("Payment status:", status);
          console.log("Verification status:", verificationStatus);
          console.log("Status type:", typeof status);
          console.log("Status === 'completed':", status === "completed");

          setPaymentStatus(status);

          // Check both status and verificationStatus
          if (status === "completed" || verificationStatus === "verified") {
            console.log("✅ Payment completed! Will redirect in 5 seconds");

            // Update status to show success
            setPaymentStatus("completed");
            toast.success(
              "Payment completed successfully! Redirecting in 5 seconds..."
            );

            // Wait 5 seconds before closing modal and redirecting
            setTimeout(() => {
              setShowWaitingModal(false);
              onPaymentSuccess();
              onClose();
            }, 5000);

            return; // Stop polling
          } else if (status === "failed") {
            console.log("❌ Payment failed");
            toast.error("Payment failed. Please try again.");
            setShowWaitingModal(false);
            return; // Stop polling
          } else {
            console.log(
              `⏳ Payment status is '${status}', will poll again in 3 seconds`
            );
          }
          // Continue polling if still pending
        } else {
          console.error("❌ Invalid response from getPaymentStatus:", response);
        }
      } catch (error) {
        console.error("❌ Error polling payment status:", error);

        // If we get an auth error, the payment might be completed but user needs to refresh
        if (
          (error as any).response?.status === 403 ||
          (error as any).response?.status === 401
        ) {
          console.log("Auth error - payment may be completed, refreshing page");
          toast("Payment processed. Refreshing page...");
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        }
      }

      // Stop polling after max attempts
      if (pollCount >= maxPolls) {
        console.log("⏱️ Max poll attempts reached");
        toast.error(
          "Payment verification taking longer than expected. Please refresh the page."
        );
        setShowWaitingModal(false);
      }
    };

    // Poll immediately and then every 3 seconds
    console.log("🚀 Starting payment status polling...");
    pollPaymentStatus();
    const interval = setInterval(pollPaymentStatus, 3000);

    return () => {
      console.log("🛑 Stopping payment status polling");
      clearInterval(interval);
    };
  }, [showWaitingModal, paymentId, onPaymentSuccess, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!amount || !phoneNumber) {
      toast.error("Please fill in all required fields");
      return;
    }

    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount) || numericAmount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (numericAmount > maxAmount) {
      toast.error("Amount cannot exceed the outstanding balance");
      return;
    }

    // Validate phone number (Kenyan format)
    const cleanPhone = phoneNumber.replace(/\D/g, "");
    if (!cleanPhone.match(/^(254|0)?[17]\d{8}$/)) {
      toast.error(
        "Please enter a valid Kenyan phone number (07xxxxxxxx or 254xxxxxxx)"
      );
      return;
    }

    try {
      setIsProcessing(true);

      // Format phone number to international format
      const formattedPhone = cleanPhone.startsWith("254")
        ? cleanPhone
        : cleanPhone.startsWith("0")
        ? `254${cleanPhone.slice(1)}`
        : `254${cleanPhone}`;

      const response = await initiateStudentEquityPayment({
        amount: numericAmount,
        phoneNumber: formattedPhone,
        studentId: studentInfo.id,
      });

      if (response.success) {
        // Store payment ID for status polling
        setPaymentId(response.data.paymentId);
        setPaymentStatus("pending");
        setShowWaitingModal(true);
        setIsProcessing(false);
        // Don't close modal or call onPaymentSuccess yet - wait for callback
      } else {
        throw new Error(response.message || "Failed to initiate payment");
      }
    } catch (error: any) {
      console.error("Equity payment error:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to initiate Equity payment. Please try again."
      );
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  // Show waiting modal after payment initiation
  if (showWaitingModal) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600 animate-spin" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Processing Payment
                </h2>
                <p className="text-sm text-gray-600">{studentInfo.name}</p>
              </div>
            </div>
            <button
              onClick={() => {
                setShowWaitingModal(false);
                resetModal();
                onClose();
              }}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 text-center">
            <div
              className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                paymentStatus === "completed" ? "bg-green-100" : "bg-blue-100"
              }`}
            >
              {paymentStatus === "completed" ? (
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              ) : (
                <Clock className="w-8 h-8 text-blue-600 animate-spin" />
              )}
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {paymentStatus === "completed"
                ? "Payment Successful!"
                : "Waiting for Payment"}
            </h3>

            <p className="text-gray-600 mb-4">
              {paymentStatus === "completed"
                ? "Your payment has been processed successfully. Thank you!"
                : "Please complete the payment on your phone. We're monitoring the status..."}
            </p>

            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">Amount:</span>
                <span className="font-semibold">
                  {formatKSh(parseFloat(amount) || 0)}
                </span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">Phone:</span>
                <span className="font-semibold">{phoneNumber}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Status:</span>
                <span
                  className={`font-semibold ${
                    paymentStatus === "completed"
                      ? "text-green-600"
                      : paymentStatus === "failed"
                      ? "text-red-600"
                      : "text-blue-600"
                  }`}
                >
                  {paymentStatus === "pending"
                    ? "Processing..."
                    : paymentStatus === "completed"
                    ? "Successful! ✓"
                    : paymentStatus}
                </span>
              </div>
            </div>

            <p className="text-xs text-gray-500">
              {paymentStatus === "completed"
                ? "Redirecting in 5 seconds..."
                : "This window will automatically close when payment is confirmed."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Building className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                M-Pesa Payment (Equity Settlement)
              </h2>
              <p className="text-sm text-gray-600">{studentInfo.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Amount (KES)
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="Enter amount"
              min="1"
              max={maxAmount}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Maximum: {formatKSh(maxAmount)}
            </p>
          </div>

          {/* M-Pesa Phone Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              M-Pesa Phone Number
            </label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="0712345678 or 254712345678"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter the phone number registered with M-Pesa for STK Push
            </p>
          </div>

          {/* Payment Methods Info */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-900 mb-2">
              Available Payment Methods
            </h3>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <CreditCard className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-gray-600">
                  Credit/Debit Cards (Visa, Mastercard)
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Building className="w-4 h-4 text-red-600" />
                <span className="text-sm text-gray-600">
                  Equity Bank Account
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Smartphone className="w-4 h-4 text-green-600" />
                <span className="text-sm text-gray-600">M-Pesa, Equitel</span>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isProcessing}
            className="w-full bg-red-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isProcessing
              ? "Processing..."
              : `Pay ${formatKSh(parseFloat(amount) || 0)}`}
          </button>
        </form>

        {/* Footer */}
        <div className="px-6 pb-6">
          <p className="text-xs text-gray-500 text-center">
            You will receive an M-Pesa STK Push notification on your phone to
            complete the payment. Funds will settle to your Equity Bank account.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EquityPaymentModal;
